import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import useItem from 'common/hooks/useItem';
import useRouter from 'common/hooks/use-router';
import DetailHeader from 'common/components/DetailHeader/DetailHeader';
import HeaderButtons from 'common/components/DetailHeader/HeaderButtons';
import SectionTitle from 'common/components/SectionTitle/SectionTitle';
import PageLoading from 'common/components/PageLoading/PageLoading';
import Icon from 'components/Icon/Icon';
import AlertsContext from 'common/providers/alerts';
import { Profile } from 'types/Profile';
import fetchJSON from 'common/utils/fetchJSON';
import EventStatusBadge from 'pages/Agenda/components/EventStatusBadge';

function StudentDetails({ userId }: { userId?: string }) {
  const router = useRouter();
  const id = userId ?? (router.query.id as string);
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);

  const { item: user, isFetching, error, fetchItem }: any = useItem('users', id, {});

  useEffect(() => {
    if (error) {
      setAlert(error, 'danger');
    }
  }, [error, setAlert]);

  const editedProfile = user?.profiles?.[0];

  const {
    username,
    firstname,
    lastname,
    dateOfBirth,
    establishment,
    training,
    updatedAt,
    createdAt,
    blocked,
    phone,
    isValidStudent,
  } = (editedProfile as Profile) || {};

  const toggleValidStudent = useCallback(async () => {
    try {
      if (editedProfile) {
        await fetchJSON({
          method: 'PUT',
          url: `profiles/${editedProfile.id}`,
          payload: { data: { isValidStudent: !isValidStudent } },
        });
        setAlert(
          t(isValidStudent ? 'status.student-not-validated' : 'status.student-validated'),
          'success'
        );
        fetchItem(id);
      }
    } catch (err) {
      setAlert(t('common.error'), 'danger');
    }
  }, [editedProfile, isValidStudent, setAlert, t, fetchItem, id]);

  const StudentHeaderInfos = useMemo(
    () => (
      <div className="w-full">
        <div className="flex items-center text-sm gap-2 text-gray-500">
          <Icon name="ClockIcon" className="w-4 h-4" />
          {dayjs(updatedAt).fromNow()}
        </div>
        <div className="responsive-flex items-center gap-3">
          <div className="flex flex-row items-center text-gray-800">
            <div className="flex flex-row items-center gap-2">
              <Icon name="EnvelopeIcon" className="w-4 h-4" />
              <span>{user?.email || t('forms.noEmail')}</span>
            </div>
          </div>
        </div>
      </div>
    ),
    [user?.email, t, updatedAt]
  );

  if (isFetching && !user) {
    return <PageLoading />;
  }

  return (
    <div className="mx-auto max-w-full py-6 px-6 text-gray-500 text-base">
      <DetailHeader
        backLink="/students"
        title={`${username}`}
        subtitleIcon="UserIcon"
        HeaderInfos={StudentHeaderInfos}
        SubTitle={<span className="font-bold">{`${firstname} ${lastname}`}</span>}
        HeaderButtons={
          <HeaderButtons
            editUrl={`/students/edit/${id}`}
            onValidate={toggleValidStudent}
            validateDisabled={isValidStudent}
            validateLabelKey="actions.validateStudent"
          />
        }
      />

      <div className="responsive-flex gap-6 mt-2">
        <div className="md:w-2/3 flex flex-col gap-6">
          <div className="bg-white p-4 rounded-2xl shadow-md">
            <div className="responsive-flex gap-8">
              <div className="flex flex-col w-full md:w-[180px] mx-3">
                <Icon
                  name="UserIcon"
                  className="w-full text-center w-24 h-24 rounded-sm p-1 mr-0.5 text-gray-500"
                />
                <div className="mt-1 mb-4 flex items-center justify-center gap-1 leading-[0] text-gray-500 shrink-0 font-normal pb-1">
                  {user?.isMediator ? t('roles.Mediator') : t('roles.Authenticated')}
                </div>
                <div className="text-xs text-center w-full px-1 pt-0 pb-4">
                  {t('forms.subscribedThe', {
                    since: dayjs(createdAt).format('DD/MM/YYYY'),
                  })}
                </div>

                <div className="flex flex-row items-center my-1 gap-2">
                  {blocked ? (
                    <Icon name="XCircleIcon" className="w-5 h-5 text-red-500" />
                  ) : (
                    <Icon name="CheckCircleIcon" className="w-5 h-5 text-green-500" />
                  )}
                  <span className="text-sm">
                    {blocked ? t('forms.blocked') : t('forms.active')}
                  </span>
                </div>

                <div className="flex flex-row items-center my-1 gap-2">
                  {user?.confirmed ? (
                    <Icon name="CheckCircleIcon" className="w-5 h-5 text-green-500" />
                  ) : (
                    <Icon name="XCircleIcon" className="w-5 h-5 text-red-500" />
                  )}
                  <span className="text-sm">{t('forms.confirmed')}</span>
                </div>

                <div className="flex flex-row items-center my-1 gap-2">
                  {isValidStudent ? (
                    <Icon name="CheckCircleIcon" className="w-5 h-5 text-green-500" />
                  ) : (
                    <Icon name="XCircleIcon" className="w-5 h-5 text-red-500" />
                  )}
                  <span className="text-sm">{t('forms.validStudent')}</span>
                </div>
              </div>
              <div className="flex flex-col w-full gap-2">
                <div className="flex flex-col mb-3">
                  <div className="text-sm font-medium text-gray-500 mb-1">
                    {t('forms.username')}
                  </div>
                  <div className="text-base font-bold text-gray-700">{username || '-'}</div>
                </div>
                <div className="flex flex-col xl:flex-row gap-2">
                  <div className="flex flex-col mb-3 flex-1 xl:min-w-[200px]">
                    <div className="text-sm text-gray-500 font-medium mb-1">
                      {t('forms.firstname')}
                    </div>
                    <div className="text-base font-bold text-gray-700">{firstname || '-'}</div>
                  </div>
                  <div className="flex flex-col mb-3 flex-1 xl:min-w-[200px]">
                    <div className="text-sm text-gray-500 font-medium mb-1">
                      {t('forms.lastname')}
                    </div>
                    <div className="text-base font-bold text-gray-700">{lastname || '-'}</div>
                  </div>
                </div>
                <div className="flex flex-col mb-3 flex-1 xl:min-w-[200px]">
                  <div className="text-sm text-gray-500 font-medium mb-1">
                    {t('forms.dateOfBirth')}
                  </div>
                  <div className="text-base font-bold text-gray-700">
                    {dayjs(dateOfBirth).format('DD MMMM YYYY') || '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl shadow-md flex flex-col gap-4">
            <SectionTitle
              title={t('forms.meetingsHistory')}
              icon="TbCalendarEvent"
              className="text-fourth mt-2"
            />
            <div className="flex flex-col w-full mp-4 gap-2">
              {user?.profiles?.[0]?.meetings ? (
                user?.profiles?.[0]?.meetings.map((meeting: any) => (
                  <div
                    key={meeting.id}
                    className="flex flex-row justify-between gap-2 flex-1 border-b border-gray-200 items-center pb-1 -mt-1 px-1 first:border-y first:pt-1"
                  >
                    <div className="flex flex-row items-center gap-2">
                      <Icon name="TbCalendarEvent" className="w-4 h-4" />
                      <span>{dayjs(meeting.date).format('DD MMMM YYYY')}</span>
                    </div>
                    <div className="flex flex-row items-center gap-2">
                      <Icon name="UserIcon" className="w-4 h-4" />
                      <span>{meeting.operator?.username || '-'}</span>
                    </div>
                    <EventStatusBadge event={{ meeting, eventType: 'meeting' }} outlined={false} />
                  </div>
                ))
              ) : (
                <p>{t('common.noResult')}</p>
              )}
            </div>
          </div>
        </div>

        <div className="md:w-1/3 flex flex-col gap-4">
          <div className="bg-white p-4 rounded-2xl shadow-md">
            <SectionTitle
              title={t('forms.contactInfos')}
              icon="UserIcon"
              className="text-fourth mt-2"
            />
            <div className="flex flex-col w-full ml-4">
              <div className="responsive-flex gap-2">
                <div className="flex flex-col w-full gap-2">
                  <div className="flex flex-row flex-wrap gap-2">
                    <div className="flex flex-col mb-3 flex-1 min-w-[200px]">
                      <div className="text-sm text-gray-500 font-medium mb-1">
                        {t('forms.phone')}
                      </div>
                      <div className="text-base font-bold text-gray-700">{phone || '-'}</div>
                    </div>
                    <div className="flex flex-col mb-3 flex-1 min-w-[200px]">
                      <div className="text-sm text-gray-500 font-medium mb-1">
                        {t('forms.email')}
                      </div>
                      <div className="text-base font-bold text-gray-700">{user?.email || '-'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-2xl shadow-md flex flex-col gap-4">
            <SectionTitle
              title={t('forms.academicInfos')}
              icon="EstablishmentIcon"
              className="text-fourth mt-2"
            />
            <div className="flex flex-col w-full ml-4 gap-2">
              <div className="flex flex-col mb-3">
                <div className="flex flex-row items-center gap-2">
                  <Icon name="IoLocationOutline" className="w-4 h-4" />
                  <div className="text-sm text-gray-500 font-medium mb-1">
                    {t('forms.department')}
                  </div>
                </div>
                <div className="text-base font-bold text-gray-700">
                  {establishment?.department
                    ? `${establishment.department.name} (${String(
                        establishment.department.CP
                      ).padStart(2, '0')})`
                    : '-'}
                </div>
              </div>
              <div className="flex flex-col mb-3">
                <div className="flex flex-row items-center gap-2">
                  <Icon name="EstablishmentIcon" className="w-4 h-4" />
                  <div className="text-sm text-gray-500 font-medium mb-1">
                    {t('forms.establishment')}
                  </div>
                </div>
                <div className="text-base font-bold font-bold text-gray-700">
                  {establishment ? establishment.name : '-'}
                </div>
              </div>
              <div className="flex flex-col mb-3">
                <div className="flex flex-row items-center gap-2">
                  <Icon name="Training" className="w-4 h-4" />
                  <div className="text-sm text-gray-500 font-medium mb-1">
                    {t('forms.training')}
                  </div>
                </div>
                <div className="text-base font-bold text-gray-700">{training || '-'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentDetails;
